// src/store/modules/sendbird/normalize.js
export function normalizeServiceHistory(rawData, language = 'en', userProfile = {}) {
  // Helper to normalize the server's chat history shape into our standard message shape.
  // rawData looks like { Dates: [ { Date, Messages: [...] } ] }
  const out = [];
  if (!rawData?.Dates?.length) return out;

  const langLc = language.toLowerCase();
  const two = langLc.slice(0, 2);

  for (const date of rawData.Dates) {
    const dateStr = date?.Date;
    for (const wrapper of (date?.Messages || [])) {
      const msg = wrapper?.Message || {};
      const translations = wrapper?.Translations || [];

      let text = msg.Message || '';
      const tr = translations.find(t => {
        const L = (t.Language || '').toLowerCase();
        return L === langLc || L.startsWith(two + '-');
      });
      if (tr?.Message) text = tr.Message;

      const createdAtStr = `${dateStr ?? ''} ${msg.DateTime ?? ''}`.trim();
      const createdAtTs = Date.parse(createdAtStr) || Date.now();

      const isMine = (userProfile?.email || '').toLowerCase() === (msg.Username || '').toLowerCase();
      const type = msg.Source === 'user' ? 'sent' : 'received';
      const name = msg.Source === 'user'
        ? (isMine ? 'me' : (msg.Username || 'user'))
        : (msg.Operator || 'operator');

      out.push({
        text,
        message: text,
        messageType: 'text',
        contentType: 'text',
        type, // 'sent' | 'received'
        name,
        sender: { userId: msg.Username, name },
        email: msg.Username,
        createdAt: createdAtTs,
        createdAtStr,
        messageId: msg.Id,
        id: msg.Id,
        raw: wrapper,
      });
    }
  }

  return out;
}

/**
 * Normalize a list of Sendbird SDK messages into the SAME shape
 * as normalizeServiceHistory() returns, so the UI can treat both
 * sources identically.
 */
export function normalizeSendbirdMessages(list = [], uiLang = 'en', userProfile = {}) {
  const me = (userProfile?.email || '').toLowerCase();

  return (list || []).map(m => {
    const senderId = (m?.sender?.userId || '').toLowerCase();
    const isMine = senderId === me;
    const name = isMine ? 'me' : (m?.sender?.nickname || m?.sender?.userId || 'user');
    const createdAt = m?.createdAt || Date.now();
    const id =
      m?.messageId ??
      m?.reqId ??
      m?.requestId ??
      `${createdAt}-${m?.sender?.userId || 'unknown'}`;

    let text = '';
    let messageType = 'text'; // 'text' | 'file'
    let contentType = 'text'; // 'text' | 'file' | 'unknown'

    if (m.messageType === 'user') {
      text = m.message || ''; // after translation step (done in action)
      messageType = 'text';
      contentType = 'text';
    } else if (m.messageType === 'file') {
      text = m.name || m.url || '[File]';
      messageType = 'file';
      contentType = m.type || 'file';
    } else {
      text = m.message || '[Unsupported]';
      messageType = 'text';
      contentType = 'unknown';
    }

    return {
      text,
      message: text,
      messageType, // 'text' | 'file'
      contentType, // 'text' | 'file' | 'unknown'
      type: isMine ? 'sent' : 'received',
      name,
      sender: { userId: m?.sender?.userId, name },
      email: m?.sender?.userId,
      createdAt,
      createdAtStr: new Date(createdAt).toISOString(),
      messageId: id,
      id,
      raw: m, // keep raw SDK message for debugging
    };
  });
}
